/* eslint-disable no-console */
import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useStateWithLocalStorage, useStateWithSessionStorage } from '../utils';

const AuthContext = createContext({});
const isBrowser = typeof window !== 'undefined';

const axiosInstance = axios.create({
  baseURL: process.env.GATSBY_API_URL,
  headers: { 'Content-Type': 'application/json' }
});

// Restore the Authorization header synchronously at module load time so that
// requests made in child useEffects on first render already carry the token —
// before AuthContextProvider's own useEffect has a chance to fire.
// Restore the Authorization header synchronously at module load time so that
// requests made in child useEffects on first render already carry the token —
// before AuthContextProvider's own useEffect has a chance to fire.
if (isBrowser) {
  try {
    const stored = JSON.parse(sessionStorage.getItem('token'));
    if (stored) {
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${stored}`;
    }
  } catch {
    // malformed value — ignore
  }
}

export const AuthContextProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useStateWithLocalStorage('user', {
    first_name: 'John',
    last_name: 'Doe',
    role: 'admins:ADMIN',
    permission: {
      grant: [],
      permission_label: ''
    }
  });
  const [token, setToken] = useStateWithSessionStorage('token');
  const [isValid, setIsValid] = useState(false);

  // Restore the Authorization header and isValid flag on mount (and keep them
  // in sync whenever the token changes). Without this, a page refresh leaves
  // isValid=false even though the token is still in sessionStorage, which
  // makes authenticated components treat the user as logged out.
  useEffect(() => {
    if (token) {
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
      setIsValid(true);
    } else {
      delete axiosInstance.defaults.headers.common.Authorization;
      setIsValid(false);
    }
  }, [token]);

  const setSession = (accessToken) => {
    if (accessToken) {
      setToken(accessToken);
      setIsValid(true);
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
      setToken(null);
      setIsValid(false);
      delete axiosInstance.defaults.headers.common.Authorization;
    }
  };
  const logout = () => {
    setSession(null);
    setUser(null);
  };

  const fetchAPI = async (
    url,
    method = 'GET',
    body = null,
    responseType = 'json',
    cancelToken
  ) => {
    try {
      setIsLoading(true);
      const result = await axiosInstance({
        url,
        method,
        responseType,
        cancelToken,
        data: body
      });
      setIsLoading(false);
      return result;
    } catch (e) {
      setIsLoading(false);
      throw e;
    }
  };
  const loginAPI = async (email, password) => {
    const result = await axiosInstance.post('/login', {
      email,
      password
    });
    setUser(result.data.user);
    setSession(result.data.token);
    return result;
  };
  const registerAPI = async (values) => {
    try {
      return await axiosInstance.post('/register', values);
    } catch (e) {
      console.log('e:', e);
    }
  };

  const dispatchAPI = (type, options) => {
    switch (type) {
      case 'LOGIN':
        return loginAPI(options.email, options.password);
      case 'LOGOUT':
        return logout();
      case 'REGISTER':
        return registerAPI(options);
      case 'GET':
        return fetchAPI(
          options.url,
          'GET',
          null,
          options.responseType,
          options.cancelToken
        );
      case 'DELETE':
        return fetchAPI(options.url, 'DELETE');
      case 'POST':
      case 'PATCH':
        return fetchAPI(options.url, type, options.body);
      default:
        console.error('Unknown dispatchAPI type!');
    }
  };

  return (
    <AuthContext.Provider
      value={{ dispatchAPI, isLoading, user, isValid, token }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthContextProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default () => useContext(AuthContext);
